import { createSocket } from './socket';
import { createCollaborationStore } from './store';
import { createHistoryLog } from './historyLog';
import {
  ChannelData,
  EventPayload,
  GetResyncStatusResponse,
  ICollaborationController,
  Metadata,
  SocketConfig,
} from './collaboration.types';
import { SOCKET_ERRORS, SOCKET_EVENTS } from './socket.config';

/**
 * CollaborationController is the main class to use the RTC feature.
 * rtc() is the main function to initialize socket connection.
 * Editor socket events are handled in this controller and basic socket events are handled in this socket.ts.
 */
class CollaborationController implements ICollaborationController {
  private _localLastSyncTime: Date;
  private _store;
  private _enableDebugMessage;
  private _callbacks: { [key in SOCKET_EVENTS]?: Array<(...args: EventPayload[]) => void> };
  private _isTeamShared: boolean;
  private _socket;
  private _socketConfig: SocketConfig;
  private _metadata: Metadata;
  private _currentUserId: string;
  private _onError: (errorType: SOCKET_ERRORS) => void;

  /**
   * TODO: Need to be implemented later
   * Record the historyLog so that connected users can share the undo/redo history.
   * It is not currently used, it can be used later when the RTC editor function is added.
   * @see assets-repo: infograph/editor/src/collaboration
   */
  private historyLog;

  /**
   * Constructor of CollaborationController to use the RTC feature.
   * @param userId
   * @param metadata
   * @param socketConfig
   */
  constructor(userId: string, metadata: Metadata, socketConfig: SocketConfig) {
    this._socketConfig = socketConfig;
    this._metadata = metadata;
    this._currentUserId = userId;

    // Handlers for out of sync detection.
    this._localLastSyncTime = new Date();

    // Create socket.io instance
    this._socket = createSocket(this as ICollaborationController);

    this._store = createCollaborationStore();

    // Enable console.log debug message
    this._enableDebugMessage = false;

    // Initialize the _callbacks object
    this._callbacks = {};

    this._isTeamShared = false;

    this._onError = (errorType: SOCKET_ERRORS) => {};

    /**
     * Need to be implemented later
     */
    // A global collabortion log, will use for detecting history out of sync
    this.historyLog = createHistoryLog();
  }

  /**
   * Getter & Setter
   */
  public get isTeamShared(): boolean {
    return this._isTeamShared;
  }
  public set isTeamShared(value: boolean) {
    this._isTeamShared = value;
  }
  public get socketConfig(): SocketConfig {
    return this._socketConfig;
  }
  public get metadata(): Metadata {
    return this._metadata;
  }
  public get store() {
    return this._store;
  }
  public get hasSocketUsers() {
    return this._store.hasSocketUsers();
  }
  public set onError(callback: (errorType: SOCKET_ERRORS) => void) {
    this._onError = callback;
  }
  public get onError() {
    return this._onError;
  }

  /**
   * Enable or disable request to resync infographic with MM
   * If it is enabled, it will request resync status
   * @param {boolean} willRequestResyncStatus
   */
  enableSocketResync(enable: boolean) {
    this._socket.io.opts.query.requestResync = enable;
  }

  updateSocketLastSyncTime() {
    this._socket.io.opts.query._localLastSyncTime = this._localLastSyncTime.getTime();
  }

  /**
   * Enable editor rtc communication, this method can move into constructor once we release 100%
   */
  rtc() {
    this._isTeamShared = true;

    // Listen for resync response: 'ok' or 'outofsync'
    this._socket.on(SOCKET_EVENTS.GET_RESYNC_STATUS, (payload: GetResyncStatusResponse) => {
      this.debug(SOCKET_EVENTS.GET_RESYNC_STATUS, payload);

      if (!payload || payload.status !== 'ok') {
        // If not in sync, trigger RESYNC_NEEDED_ERROR error to display out of sync modal.
        this.onError(SOCKET_ERRORS.RESYNC_NEEDED_ERROR);
      }
    });

    /**
     * Another user has entered the editor
     */
    this._socket.on(SOCKET_EVENTS.ENTER_EDITOR, (payload: ChannelData) => {
      this.debug(SOCKET_EVENTS.ENTER_EDITOR, payload);
      if (payload.user_id === this._currentUserId) return;

      // Add new editor
      const socketUser = this._store.addSocketUser(payload);
      if (!socketUser) return;

      // Fire _callbacks that were bound with CollaborationController.on(ENTER_EDITOR...
      this.fireCallbacks(SOCKET_EVENTS.ENTER_EDITOR, [socketUser as EventPayload]);
    });

    /**
     * Another user has left the editor
     */
    this._socket.on(SOCKET_EVENTS.LEAVE_EDITOR, (payload: ChannelData) => {
      this.debug(SOCKET_EVENTS.LEAVE_EDITOR, payload);
      const socketUser = this._store.getSocketUser(payload.socketId);

      if (!socketUser) return;

      // Remove editor
      this._store.removeSocketUser(socketUser);

      // Update the current socket data to editor redux store
      // VGEditor.updateCollaborationSocketData();

      // Fire _callbacks that were bound with CollaborationController.on(LEAVE_EDITOR...
      this.fireCallbacks(SOCKET_EVENTS.LEAVE_EDITOR, [socketUser as EventPayload]);
    });

    /**
     * Initial user list - when on the My Designs or Editor page, this
     * event is automatically initiated when connecting. The response
     * is the list of users that already have the editor open
     */
    this._socket.on(SOCKET_EVENTS.INFOGRAPHICS_IN_EDITED, (payload: ChannelData[]) => {
      this.debug(SOCKET_EVENTS.INFOGRAPHICS_IN_EDITED, payload);

      // Add current editors
      payload.forEach((socket) => {
        if (socket.user_id === this._currentUserId) {
          return;
        }
        const socketUser = this._store.addSocketUser(socket);
        if (!socketUser) {
          return;
        }
      });

      // Fire _callbacks that were bound with CollaborationController.on(INFOGRAPHICS_IN_EDITED...
      this.fireCallbacks(SOCKET_EVENTS.INFOGRAPHICS_IN_EDITED, [payload]);
    });

    // Only one use can be editing at a time.
    this._socket.on(SOCKET_EVENTS.DISABLE_SHARE, (payload: EventPayload) => {
      // It can be implemented when we provide collaboration feature on the editor page.

      // Fire _callbacks that were bound with CollaborationController.on(DISABLE_SHARE...
      this.fireCallbacks(SOCKET_EVENTS.DISABLE_SHARE, [payload]);
    });
  }

  /*
   * Disable RTC by first notifying other users that it's been disabled,
   * then disconnecting the user's socket
   */
  disableShare(callback = () => {}) {
    // Keep an internal register that we're disconnecting and don't want to reconnect
    this.isTeamShared = false;

    // Notify other users before disconnecting
    this._socket.emit(SOCKET_EVENTS.DISABLE_SHARE, { teamShare: false });
    this._socket.disconnect();

    callback();
  }

  /**
   * Internal debug function for debugging socket events
   *
   */
  debug(...data: any[]) {
    if (!this._enableDebugMessage) return;

    console.debug('[DEBUG:CollaborationController]', ...data);
  }

  /**
   * Binds an event listener to a type of socket event, the callback will be sent
   * args depending on the type of event, and `this` will be the CollaborationController
   *
   * @param {String} event_type   The type of socket event to listen for
   * @param {Function} callback   The callback for this event,
   *
   * @example
   *   import { EVENTS } from './socket';
   *   VGEditor.collaboration.on(SOCKET_EVENTS.SAVE_WIDGETS, (action, data, user) => {
   *      console.log("Someone saved a widget", action, data, user);
   *   })
   */
  on(eventType: SOCKET_EVENTS, callback: (data: EventPayload) => void): boolean {
    if (!callback || typeof callback !== 'function') return false;

    // Ensure the array for this event type is defined before pushing
    if (!this._callbacks[eventType] || !Array.isArray(this._callbacks[eventType])) {
      this._callbacks[eventType] = [];
    }

    // Add the new callback
    this._callbacks[eventType]?.push(callback);

    return true;
  }

  /**
   * Un-binds an event listener from a socket event type
   *
   * @param {String} event_type           The type of socket event to listen for
   * @param {Function} callback           The function to unbind from this event
   */
  off(eventType: SOCKET_EVENTS, callback: (data: EventPayload) => void): boolean {
    if (typeof callback !== 'function') return false;

    // Make sure this event type can be iterated
    if (!!this._callbacks[eventType] && Array.isArray(this._callbacks[eventType])) {
      // Delete the callback from the list if we can find it
      this._callbacks[eventType] = this._callbacks[eventType]?.filter((cb) => cb !== callback) ?? [];
    }
    return true;
  }

  /**
   * Internal - when an event is bound with .on, this will fire that event based on whatever triggered it
   */
  fireCallbacks(eventType: SOCKET_EVENTS, args: EventPayload[] | undefined = [], thisObj: this | undefined = this) {
    // If there are _callbacks for this type of event...
    if (!!this._callbacks[eventType] && Array.isArray(this._callbacks[eventType])) {
      // Loop through them all and send the correct args
      this._callbacks[eventType]?.map((cb) => cb.apply(thisObj, args));
    }
  }
}

export default CollaborationController;
